import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

import { NotificationFilterBody, NotificationMessage, NotificationStatus } from 'types/Notifications';
import notificationCenterService from 'services/notificationCenterService';
import { User } from 'types/User';
import { GlobalState } from 'reducers/stateTypes';
import { updateNotifications } from 'reducers/notificationReducer';
import { openModal } from 'reducers/modalReducer';
import {
  ZfBadge,
  ZfBox,
  ZfIcon,
  ZfIconButton,
  ZfPopover,
  ZfPopoverHeader,
  ZfStack,
  ZfTooltip,
  ZfTypography,
  ZfListItem,
  ZfLoadingSpinner,
  ZfTextButton,
} from '@zafire/react';
import { NotificationRow } from 'components/Navigation/NotificationRow';
import { useHost } from 'contexts/Host';
import { usePostMessageToIFrame } from 'hooks/usePostMessageToIFrame';
import { ClmPortalOutgoingPostMessageTypes } from 'utils/postMessageUtil';

/**
 * Component to display notifications grid in the top bar as part of the account options.
 */
export const NotificationGrid = () => {
  const {
    startWebsocketClient,
    getNotificationStatusRequest,
    createNotificationStatusRequest,
    createNotificationFilterBody,
  } = notificationCenterService;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isWena } = useHost();
  const { postMessageToIFrame } = usePostMessageToIFrame();
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const clickRefresh: number = useSelector((state: GlobalState) => state.notifications.clickRefresh);
  const userId = user?.username;
  const [notificationHistory, setNotificationHistory] = useState<NotificationMessage[]>([]);
  const [disconnected, setDisconnected] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const zfPopoverRef = useRef<HTMLZfPopoverElement>(null);

  useEffect(() => {
    const handleBlur = () => {
      requestAnimationFrame(() => {
        const { activeElement } = document;

        if (activeElement && !zfPopoverRef.current?.contains(activeElement)) {
          zfPopoverRef.current?.close();
        }
      });
    };

    window.addEventListener('blur', handleBlur);

    return () => window.removeEventListener('blur', handleBlur);
  }, []);

  useEffect(() => {
    userId && getNotificationsFromServer();
  }, [userId]);

  useEffect(() => {
    if (disconnected && userId) {
      delay(500).then(() => {
        startWebsocketClient(userId, setNotificationHistory, setLoading, setErrorMessage, setDisconnected);
      });
    }
  }, [disconnected, userId]);

  const delay = (time: number) => {
    return new Promise((resolve) => setTimeout(resolve, time));
  };

  const getNotificationsFromServer = () => {
    const requestBody: NotificationFilterBody = createNotificationFilterBody(userId);
    setLoading(true);
    getNotificationStatusRequest(requestBody)
      .then((data) => {
        setLoading(false);
        setNotificationHistory(data.notificationMessages);
      })
      .catch((error) => {
        setLoading(false);
        setErrorMessage(error?.message ? error?.message : 'Error getting notifications.');
      });
  };

  const updateNotificationOnServer = (
    notificationId: string[],
    newStatus: NotificationStatus = NotificationStatus.READ,
  ) => {
    const requestBody: NotificationFilterBody = createNotificationFilterBody(userId, notificationId, newStatus);
    updateNotificationHistory(notificationId, newStatus);
    createNotificationStatusRequest(requestBody).catch((error) => {
      updateNotificationHistory(
        notificationId,
        newStatus === NotificationStatus.READ ? NotificationStatus.UNREAD : NotificationStatus.READ,
      );
      setErrorMessage(error?.message ? error?.message : 'Error updating notifications.');
    });
  };

  const updateNotificationHistory = (notificationId: string[], newStatus: NotificationStatus) => {
    setNotificationHistory(
      notificationHistory.map((notification) =>
        notificationId.indexOf(notification.id) >= 0 ? { ...notification, status: newStatus } : notification,
      ),
    );
  };

  const onNotificationClick = (notificationId: string) => {
    const notification = notificationHistory.find((notification) => notification.id === notificationId);
    if (notification) {
      if (notification.status === NotificationStatus.UNREAD) {
        updateNotificationOnServer([notificationId]);
      }
      if (notification.newTab) {
        window.open(notification.url, '_blank')?.focus();
      }
      if (notification.url.includes('pcd') || notification.url.includes('pco')) {
        const url = notification.url.includes('pcd')
          ? notification.url.split('pcd/')[1]
          : notification.url.split('pco/')[1];
        navigate(`/${url}`);
      } else {
        const notificationUrl = notification.url || '';
        const urlParams = new URLSearchParams(notificationUrl.split('?')[1] || '');
        const currentTaskId = urlParams.get('taskId') || urlParams.get('currentTaskId') || '';

        dispatch(
          updateNotifications({
            currentTaskId: currentTaskId ?? '',
            clickRefresh: clickRefresh + 1,
          }),
        );

        if (location.pathname.includes('home/tasks')) {
          const payload = {
            message: ClmPortalOutgoingPostMessageTypes.TASK_VIEWER_SEND_URL_PARAMS,
            value: `?currentTaskId=${currentTaskId}`,
          };
          const iframe = document.getElementById('home-tab-iframe') as HTMLIFrameElement;

          postMessageToIFrame(payload, iframe);
        } else {
          navigate(`home/tasks/my-tasks?currentTaskId=${currentTaskId}`);
        }
      }
    }
  };

  const onMoreIconClick = (notificationId: string, newStatus: NotificationStatus) => {
    updateNotificationOnServer([notificationId], newStatus);
  };

  const handleClickOnAllRead = () => {
    const unreadNotificationIds = notificationHistory
      .filter((notification) => notification.status === NotificationStatus.UNREAD)
      .map((notification) => notification.id);
    updateNotificationOnServer(unreadNotificationIds);
  };

  const handleClickOnSettings = () => {
    dispatch(openModal({ modalType: 'NotificationSettingsModal', modalProps: {} }));
  };

  const unreadNotificationCount = notificationHistory
    ? notificationHistory.filter((notification) => notification.status === NotificationStatus.UNREAD).length
    : 0;

  function renderRow(props: any) {
    const { index, style } = props;

    return index < notificationHistory.length ? (
      <NotificationRow
        key={notificationHistory[index].id}
        id={notificationHistory[index].id}
        isNew={notificationHistory[index].status === NotificationStatus.UNREAD}
        title={notificationHistory[index].title || ' '}
        notificationDateTime={notificationHistory[index].creationDate}
        notificationText={notificationHistory[index].content}
        onNotificationClick={onNotificationClick}
        onMoreIconClick={onMoreIconClick}
        style={style}
      />
    ) : (
      <ZfBox key="notification-list-last-item" style={style}>
        <ZfTypography
          alignment="center"
          style={{ paddingTop: `var(--padding-vertical-md)`, color: `var(--color-text-neutral-40)` }}
        >
          No further notifications. <br />
          "Read" notifications are removed after 45 days.
        </ZfTypography>
      </ZfBox>
    );
  }

  const notificationIcon = isWena ? (
    <ZfIconButton hierarchy="tertiary">
      <ZfIcon name="notifications" size="large" />
    </ZfIconButton>
  ) : (
    <ZfBox
      style={{
        border: '1px solid var(--bg-color-default-normal)',
        height: '36px',
        width: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <ZfIcon name="notifications" size="large" />
    </ZfBox>
  );

  return (
    <ZfBox>
      <ZfPopover ref={zfPopoverRef}>
        <ZfBox slot="anchor">
          {unreadNotificationCount ? (
            <ZfBadge role="note" overlap="rectangular" value={String(unreadNotificationCount)} max={99} color="error">
              {notificationIcon}
            </ZfBadge>
          ) : (
            notificationIcon
          )}
        </ZfBox>
        <ZfPopoverHeader>
          <ZfStack direction="row" alignment="center" justify="space-between">
            <ZfBox>
              <ZfTypography variant="label-medium" color="emphasis">
                Notifications
              </ZfTypography>
            </ZfBox>
            <ZfStack direction="row">
              <ZfTextButton hierarchy="tertiary" size="medium" onClick={handleClickOnAllRead}>
                Mark all as read
              </ZfTextButton>
              <ZfTooltip text="Settings...">
                <ZfIconButton slot="anchor" hierarchy="tertiary" size="medium" onClick={handleClickOnSettings}>
                  <ZfIcon name="settings" size="large" />
                </ZfIconButton>
              </ZfTooltip>
            </ZfStack>
          </ZfStack>
        </ZfPopoverHeader>
        <ZfTypography
          variant="body-medium"
          weight="regular"
          style={{ color: 'var(--fg-color-default-muted)', marginBottom: 'var(--gap-vertical-xs)' }}
        >
          Today
        </ZfTypography>
        <ZfStack direction="column">
          {errorMessage && (
            <ZfListItem
              variant="destructive"
              key="notification-error"
              style={{ borderBottom: 'solid 1px var(--color-surface-light-40)' }}
              description={`Error: ${errorMessage}`}
            />
          )}
          {notificationHistory && notificationHistory.length > 0 && (
            <ZfStack>
              <FixedSizeList
                height={300}
                width={386}
                itemSize={89}
                itemCount={notificationHistory.length + 1}
                overscanCount={7}
              >
                {renderRow}
              </FixedSizeList>
            </ZfStack>
          )}
          {loading && (
            <ZfStack alignment="center">
              <ZfLoadingSpinner aria-label="loading" size="large" color="primary" type="infinite" />
            </ZfStack>
          )}
        </ZfStack>
      </ZfPopover>
    </ZfBox>
  );
};
